import { Dialog, DialogActions, DialogContent, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { FC, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useStore } from '../../../../hooks/useStore';
import { ConfirmationDialog, ContentHead, TwoFactorCodeInput } from '../../../../lib/components';
import { ContentBox } from '../../../../lib/components/compound/pages/contentBox';
import { DataGrid, DataGridElement } from '../../../../lib/components/compound/dataGrid';
import { InformationDialogTitle } from '../../../../lib/components/compound/dialogs/informationDialog/title';
import { Box } from '@mui/system';
import { useSnackbar } from 'notistack';
import { Button } from '../../../../lib/components/atomic/button';
import { FeedbackAlert } from '../../../../lib/components/atomic/feedback';

export const ManageTwoFactorAuth: FC = observer(() => {
  const { t } = useTranslation();
  const { authenticationStore, companyStore } = useStore();
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [error, setError] = useState(false);
  const [requestCodeDisabled, setRequestCodeDisabled] = useState(false);

  const snackbar = useSnackbar();

  const email = authenticationStore.currentUser ?? '';
  const passwordLoginDeactivated =
    companyStore.settings?.platform_single_sign_on_enabled &&
    !companyStore.settings?.platform_password_auth_enabled;

  useEffect(() => {
    const fetchData = async () => {
      const data = await authenticationStore.userTwoFactorMethods();

      let emailEnabled = false;
      data?.forEach((entry) => {
        if (entry.name === 'email') emailEnabled = true;
      });
      setTwoFactorEnabled(emailEnabled);
    };

    fetchData();
  }, []);

  const submitCode = async (code: string) => {
    try {
      await authenticationStore.confirmEnableTwoFactor(code);
      setDialogOpen(false);
      setTwoFactorEnabled(true);
      snackbar.enqueueSnackbar({
        variant: 'success',
        message: t('auth:MANAGE_2FA_ENABLE_SNACKBAR')
      });
    } catch {
      setError(true);
    }
  };

  const disableTwoFactor = async () => {
    await authenticationStore.disableTwoFactor();
    setTwoFactorEnabled(false);
    snackbar.enqueueSnackbar({
      variant: 'warning',
      message: t('auth:MANAGE_2FA_DISABLE_SNACKBAR')
    });
  };

  const headerButton = twoFactorEnabled ? (
    <Button color="error" onClick={() => setDialogOpen(true)}>
      {t('common:DISABLE')}
    </Button>
  ) : (
    <Button
      onClick={async () => {
        await authenticationStore.enableTwoFactor();
        setDialogOpen(true);
      }}
      disabled={passwordLoginDeactivated}
    >
      {t('common:ENABLE')}
    </Button>
  );

  const onClose = () => setDialogOpen(false);

  const requestCode = async () => {
    let timeout = 0;

    try {
      setRequestCodeDisabled(true);
      timeout = Number(setTimeout(() => setRequestCodeDisabled(false), 60000));
      await authenticationStore.requestCode();
    } catch {
      clearTimeout(timeout);
      setRequestCodeDisabled(false);
    }
  };

  return (
    <>
      <ContentBox
        headComponent={
          <>
            <ContentHead
              headline={t('account:MANAGE_2FA')}
              showActions={true}
              renderActions={() => <Box sx={{ marginRight: '8px' }}>{headerButton}</Box>}
            />
          </>
        }
      >
        <DataGrid>
          <DataGridElement
            title={t('common:METHOD')}
            value={twoFactorEnabled ? t('auth:email') : '-'}
          />
          <DataGridElement
            title={t('common:STATUS')}
            value={twoFactorEnabled ? t('common:ENABLED') : t('common:DISABLED')}
          />
        </DataGrid>
        {passwordLoginDeactivated && (
          <Box sx={{ marginTop: '32px' }}>
            <FeedbackAlert
              title={t('common:HINT')}
              description={
                t('company:PASSWORD_DEACTIVATED_HINTS_2FA', { returnObjects: true }) as string[]
              }
              severity="info"
            />
          </Box>
        )}
      </ContentBox>
      {twoFactorEnabled ? (
        <ConfirmationDialog
          open={dialogOpen}
          onConfirm={disableTwoFactor}
          onClose={onClose}
          variant={'warning'}
          title={t('auth:MANAGE_2FA_DISABLE_HEADER')}
          contentText={t('auth:MANAGE_2FA_DISABLE_BODY')}
          confirmButtonText={t('common:DISABLE')}
        />
      ) : (
        <Dialog
          open={dialogOpen}
          onClose={onClose}
          maxWidth={'sm'}
          PaperProps={{ className: 'shadow-900' }}
        >
          <InformationDialogTitle title={t('auth:MANAGE_2FA_ENABLE_HEADER')} onClose={onClose} />
          <DialogContent
            dividers
            sx={{
              borderBottom: 'none',
              paddingTop: '24px',
              paddingBottom: '8px'
            }}
          >
            <Typography paragraph marginBottom="0">
              <Trans
                i18nKey={'auth:MANAGE_2FA_ENABLE_BODY'}
                components={{ bold: <strong /> }}
                values={{ email }}
              />
            </Typography>
            <Box display={'flex'} flexDirection={'column'}>
              <Box display={'block'} marginX={'auto'} marginTop={4} gap={0.5} width={'300px'}>
                <TwoFactorCodeInput
                  submitCode={submitCode}
                  codeValid={false} // no need to disable inputs here, since modal will close straight away
                  error={error}
                  setError={setError}
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions
            disableSpacing
            sx={{
              padding: '1.5rem',
              gap: '1rem',
              flexWrap: 'wrap'
            }}
          >
            <Button variant="secondary" onClick={onClose}>
              {t('common:CANCEL')}
            </Button>
            <Button variant="primary" onClick={requestCode} disabled={requestCodeDisabled}>
              {t('auth:LOGIN_2FA_REQUEST_CODE')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
});
